import {
  Box,
  Button,
  Chip,
  Divider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { describeError, useToast } from "./toast";
import { useTranslation } from "react-i18next";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import MessageIcon from "@mui/icons-material/ChatBubbleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import KeyIcon from "@mui/icons-material/VpnKey";
import RefreshIcon from "@mui/icons-material/Refresh";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import DescriptionIcon from "@mui/icons-material/Description";
import { useEffect, useMemo, useRef, useState } from "react";
import { foyerApi, type ScreenRecord } from "../http/foyer";
import type {
  FoyerCommand,
  ScreenHistoryEntry,
  ScreenRole,
  ScreenStatusEvent,
  DoorSignOrientation,
} from "@shared/types/foyer";
import { isScreenOnline } from "@shared/types/foyer";
import UptimeChart from "./UptimeChart";
import DeviceLogPanel from "./DeviceLogPanel";
import DeviceExitsPanel from "./DeviceExitsPanel";
import RestoreCloneDialog from "./RestoreCloneDialog";
import { PermissionGate, usePermission } from "../auth/access";
import { EditDrawer } from "./EditDrawer";
import {
  platformLabel,
  capabilityLabel,
  commandLabel,
  captureModeLabel,
  managementLabel,
  roleLabel,
  relativeTime,
  screenshotFidelityNote,
  signagePlaybackLabel,
} from "../utils/devicePresentation";
import { useFormat, type Formatters } from "../i18n/format";
import type { Translate } from "../i18n/translate";

interface Props {
  open: boolean;
  premiseId: string;
  screen: ScreenRecord | null;
  /** Whether the door-sign role is offered - true only on care premises. On a
   *  hotel the role control is hidden entirely (every screen is an in-room TV). */
  allowDoorSign?: boolean;
  /** Whether the signage role is offered - i.e. the place bought Signage. */
  allowSignage?: boolean;
  /** Whether screen sharing exists on this premise at all. False on a care
   *  premise: casting is a hospitality function, the boxes ship with Chromecast
   *  removed, and a care operator must not be shown a hotspot, a "Casting"
   *  capability or a rotate button for something their rooms cannot do.
   *  Defaults true so the chain-wide fleet view (Nenda staff, mixed premises)
   *  keeps the tool it has today. */
  showGuestCasting?: boolean;
  onClose: () => void;
  /** Called after destructive actions (unpair) so the parent refreshes. */
  onChanged?: () => void;
  /**
   * Called when the user clicks "Open preview" for this screen.
   *
   * The role travels with it because the preview it should open depends on it,
   * and because the dialog's own role mirror is fresher than `screen.role` -
   * flipping a screen to Signage and previewing it without closing the dialog
   * must not open the launcher and imply the flip did not take.
   */
  onOpenPreview: (screen: ScreenRecord, role: ScreenRole) => void;
}

/**
 * Display power state → human label. "On", "Standby (since <time>)", or "-"
 * when no power command has ever targeted the device. Reflects what the power
 * schedule has commanded (energy / ESG view).
 */
function formatPower(
  t: Translate,
  fmt: Formatters,
  state?: "on" | "standby",
  atIso?: string
): string {
  if (state === "on") return t("common:labels.on");
  if (state === "standby") {
    return atIso
      ? t("premises:deviceDetailDialog.power.standbySince", {
          at: fmt.dateTime(atIso),
        })
      : t("premises:deviceDetailDialog.power.standby");
  }
  return "-";
}

function formatUptime(t: Translate, ms?: number): string {
  if (!ms || ms < 0) return "-";
  const s = Math.floor(ms / 1000);
  const days = Math.floor(s / 86400);
  const hours = Math.floor((s % 86400) / 3600);
  const mins = Math.floor((s % 3600) / 60);
  const parts: string[] = [];
  if (days)
    parts.push(t("premises:devicePresentation.duration.days", { n: days }));
  if (hours || days)
    parts.push(t("premises:devicePresentation.duration.hours", { n: hours }));
  parts.push(t("premises:devicePresentation.duration.minutes", { n: mins }));
  return parts.join(" ");
}

/**
 * Wi-Fi RSSI (dBm) → "-55 dBm (Good)". Rough quality bands per the usual
 * Android thresholds. "-" when there's no reading (Ethernet, or signal absent).
 */
function formatSignal(t: Translate, dBm?: number): string {
  if (typeof dBm !== "number" || !Number.isFinite(dBm)) return "-";
  const quality =
    dBm >= -55
      ? "excellent"
      : dBm >= -67
        ? "good"
        : dBm >= -75
          ? "fair"
          : "weak";
  return t("premises:deviceDetailDialog.signal", {
    dBm,
    quality: t(`premises:devicesView.signal.${quality}`),
  });
}

/** 30 × 2 s: long enough for a TV to render, encode and upload a capture. */
const DIAGNOSTIC_POLLS = 30;

/**
 * What a finished command means, for the toast. "acked" alone is only true of
 * delivery: a capture acked with an error, or with no file at all, did not
 * produce anything the operator asked for and must not read as a success.
 */
type Outcome =
  | FoyerCommand["status"]
  | "failedOnDevice"
  | "received"
  | "ackedNoFile";

export function commandOutcome(c: FoyerCommand): Outcome {
  if (c.status !== "acked") return c.status;
  if (c.type !== "screenshot" && c.type !== "log_capture") return "acked";
  if (typeof c.result?.error === "string") return "failedOnDevice";
  if (typeof c.result?.assetUrl === "string") return "received";
  return "ackedNoFile";
}

export function outcomeLabel(t: Translate, c: FoyerCommand): string {
  return t(`premises:deviceDetailDialog.outcome.${commandOutcome(c)}`);
}

/** Chip colour for one command: a capture that came back empty is a failure
 *  whatever its delivery status says. */
function commandChipColor(
  c: FoyerCommand
): "success" | "warning" | "error" | "default" {
  const outcome = commandOutcome(c);
  return outcome === "acked" || outcome === "received"
    ? "success"
    : c.status === "acked"
      ? "error"
      : commandStatusColor(c.status);
}

/** Delivery-status → MUI chip colour. */
function commandStatusColor(
  status: FoyerCommand["status"]
): "success" | "warning" | "error" | "default" {
  switch (status) {
    case "acked":
      return "success";
    case "sent":
    case "queued":
      return "warning";
    case "failed":
    case "expired":
      return "error";
    default:
      return "default";
  }
}

/**
 * Plain-language rendering of one audited device event. Staff read this to
 * answer "why did this TV change?", so a re-attach names the factor that
 * justified it - that is the whole point of auditing an action no human
 * approved. Unknown verbs fall through to the raw action rather than being
 * hidden: an event we forgot to describe must still be visible.
 */
function describeHistory(t: Translate, h: ScreenHistoryEntry): string {
  const ctx = h.context ?? {};
  const str = (k: string): string | null =>
    typeof ctx[k] === "string" ? (ctx[k] as string) : null;

  switch (h.action) {
    case "screen.reattach.serial": {
      const factorKey = {
        token_possession: "tokenPossession",
        provisioned_expectation: "provisionedExpectation",
        reattach_window: "reattachWindow",
        known_network: "knownNetwork",
      }[str("factor") ?? ""];
      const how = factorKey
        ? t(`premises:deviceDetailDialog.history.factor.${factorKey}`)
        : str("factor");
      const room = str("roomId");
      return room
        ? t("premises:deviceDetailDialog.history.reattachedToRoom", {
            room,
            how,
          })
        : t("premises:deviceDetailDialog.history.reattached", { how });
    }
    case "screen.reattach_window.open":
      return ctx.minutes
        ? t("premises:deviceDetailDialog.history.windowOpenedMinutes", {
            actor: h.actor,
            minutes: String(ctx.minutes),
          })
        : t("premises:deviceDetailDialog.history.windowOpened", {
            actor: h.actor,
          });
    case "checkout.wipe.device":
      return h.outcome === "success"
        ? t("premises:deviceDetailDialog.history.wipeRan")
        : t("premises:deviceDetailDialog.history.wipeUnconfirmed");
    case "checkin.signal.device":
      return t("premises:deviceDetailDialog.history.checkInSignalled");
    default:
      return `${h.action}${h.outcome !== "success" ? ` (${h.outcome})` : ""}`;
  }
}

export default function DeviceDetailDialog({
  open,
  premiseId,
  screen: screenProp,
  allowDoorSign = false,
  allowSignage = false,
  showGuestCasting = true,
  onClose,
  onChanged,
  onOpenPreview,
}: Props) {
  const here = useMemo(() => ({ premiseId }), [premiseId]);
  const canSeeHistory = usePermission("screens", "edit", here);
  // The server returns the hotspot to edit on screens, the same level.
  const showHotspot = showGuestCasting && canSeeHistory;
  // The callers clear `screen` the moment they close the panel (onClose,
  // Unpair, Open preview). Keep showing the last one so the drawer slides out
  // with its content instead of vanishing.
  const lastScreen = useRef<ScreenRecord | null>(screenProp);
  if (screenProp) lastScreen.current = screenProp;
  const screen = screenProp ?? lastScreen.current;
  // What the panel itself last said it was doing. Only a signage screen
  // reports; everything else has nothing to show and shows nothing.
  const { t } = useTranslation(["premises", "common"]);
  const fmt = useFormat();
  const playback = signagePlaybackLabel(t, screen?.live?.signage);
  const [events, setEvents] = useState<ScreenStatusEvent[]>([]);
  const [history, setHistory] = useState<ScreenHistoryEntry[]>([]);
  const [commands, setCommands] = useState<FoyerCommand[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const toast = useToast();
  const deviceName = screenProp?.roomId
    ? t("deviceDetailDialog.deviceName", { room: screenProp.roomId })
    : t("deviceDetailDialog.deviceNameFallback");
  const [cloneOpen, setCloneOpen] = useState(false);
  // Local copy so a regenerate updates the display without a full refetch.
  const [hotspot, setHotspot] = useState(screen?.guestHotspot);
  // What the screen is. Read-only (Sigge, 2026-10-19): the type of the room it
  // is connected to decides it, set under Rooms - there is no switch here.
  const role: ScreenRole = screen?.role ?? "tv";
  // Mount orientation for a door sign - landscape (default) vs portrait. Local
  // mirror, same pattern as `role`.
  const [orientation, setOrientation] = useState<DoorSignOrientation>(
    screen?.doorSignOrientation ?? "landscape"
  );

  function loadCommands(screenId: string) {
    foyerApi
      .screenCommands(screenId, 10)
      .then(setCommands)
      .catch(() => setCommands([]));
  }

  useEffect(() => {
    if (!open || !screen) return;
    setHotspot(screen.guestHotspot);
    setOrientation(screen.doorSignOrientation ?? "landscape");
    foyerApi
      .screenStatusLog(screen.id, 14)
      .then(setEvents)
      .catch(() => setEvents([]));
    if (canSeeHistory) {
      foyerApi
        .screenHistory(premiseId, screen.id, 20)
        .then(setHistory)
        .catch(() => setHistory([]));
    } else {
      setHistory([]);
    }
    loadCommands(screen.id);
  }, [open, screen, premiseId, canSeeHistory]);

  if (!screen) return null;

  const live = screen.live;
  const online = isScreenOnline(screen, 60_000);
  // The box reports what its firmware can do; a care premise is not shown the
  // one capability it is not allowed to use (see `showGuestCasting`). The device
  // still reports it - this hides the chip, it does not rewrite telemetry.
  const capabilities = (live?.capabilities ?? []).filter(
    (c) => showGuestCasting || c !== "guestCasting"
  );

  async function withBusy<T>(
    label: string,
    fn: () => Promise<T>,
    // The command already toasted something more specific itself.
    quietSuccess = false
  ) {
    setBusy(label);
    try {
      await fn();
      if (!quietSuccess)
        toast.success(
          t("deviceDetailDialog.sent", { label, device: deviceName })
        );
      if (screen) loadCommands(screen.id);
    } catch (err) {
      toast.error(
        describeError(t, "runDeviceCommand", err, {
          label,
          device: deviceName,
        })
      );
    } finally {
      setBusy(null);
    }
  }

  const reboot = () =>
    withBusy(t("deviceDetailDialog.labels.reboot"), () =>
      foyerApi.rebootScreen(premiseId, screen.id)
    );

  const sendMessage = () => {
    const body = prompt(t("deviceDetailDialog.messagePrompt"));
    if (!body) return;
    return withBusy(t("deviceDetailDialog.labels.sendMessage"), () =>
      foyerApi.messageBulk({
        premiseId,
        screenIds: [screen.id],
        body,
        title: "Reception",
      })
    );
  };

  const refreshTelemetry = () =>
    withBusy(t("deviceDetailDialog.labels.refreshTelemetry"), () =>
      foyerApi.refreshTelemetry(premiseId, screen.id)
    );

  // Issue a diagnostic command, then poll the command log until the device has
  // uploaded + acked it (the result carries the asset URL). The device usually
  // responds in a second or two; we cap the wait so the UI never hangs.
  async function issueAndPoll(
    label: string,
    fn: () => Promise<{ commandId: string }>,
    // A diagnostic capture is a render + an upload from a TV-class CPU, and
    // routinely outlives the 24 s a plain command gets. Polling for less than
    // that is how a working capture came to read as "sent" forever.
    polls = 12
  ) {
    if (!screen) return;
    setBusy(label);
    try {
      const { commandId } = await fn();
      loadCommands(screen.id);
      for (let i = 0; i < polls; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const cmds = await foyerApi.screenCommands(screen.id, 10);
        setCommands(cmds);
        const done = cmds.find((c) => c.commandId === commandId);
        if (
          done &&
          (done.status === "acked" ||
            done.status === "failed" ||
            done.status === "expired")
        ) {
          const outcome = commandOutcome(done);
          const failed =
            done.status !== "acked" ||
            outcome === "failedOnDevice" ||
            outcome === "ackedNoFile";
          const text = t("deviceDetailDialog.outcomeToast", {
            label,
            device: deviceName,
            outcome: outcomeLabel(t, done),
          });
          if (failed) toast.error(text);
          else toast.success(text);
          break;
        }
        if (i === polls - 1) {
          toast.error(
            t("deviceDetailDialog.noResponse", { label, device: deviceName })
          );
        }
      }
    } catch (err) {
      toast.error(
        describeError(t, "runDeviceCommand", err, {
          label,
          device: deviceName,
        })
      );
    } finally {
      setBusy(null);
    }
  }

  const takeScreenshot = () =>
    issueAndPoll(
      t("deviceDetailDialog.labels.screenshot"),
      () => foyerApi.screenshotScreen(premiseId, screen.id),
      DIAGNOSTIC_POLLS
    );

  const captureLogs = () =>
    issueAndPoll(
      t("deviceDetailDialog.labels.captureLogs"),
      () => foyerApi.captureLogs(premiseId, screen.id),
      DIAGNOSTIC_POLLS
    );

  // TEP cloning: offered only where the TV reports `cloneRestore` (Samsung's
  // webapis.clone inside the signed widget). The clone file must come from a
  // reference TV of the same model + firmware (Hotel Option -> Clone TV to USB,
  // then upload `CloneEncrypted.sclone` and paste its URL here).
  const canRestoreClone = live?.capabilities?.includes("cloneRestore") === true;
  // The dialog queues the push; polling it here shows the TV's verdict in the
  // same busy/result line as every other device action.
  const onClonePushed = (commandId: string) =>
    issueAndPoll(
      t("deviceDetailDialog.labels.restoreClone"),
      () => Promise.resolve({ commandId }),
      // Download + write + restore on a TV-class CPU outlives a plain command.
      DIAGNOSTIC_POLLS
    );

  // Display power, now. Offered ONLY where the device reports `powerControl`,
  // which on Samsung means the panel answered both setPanelMute and
  // getPanelMute - i.e. the switch can be confirmed against the hardware. A
  // button that silently does nothing is worse than no button, because an
  // operator then believes the room is dark.
  const canPower = live?.capabilities?.includes("powerControl") === true;
  const displayOn = live?.powerState !== "standby";
  const togglePower = () =>
    issueAndPoll(
      displayOn
        ? t("deviceDetailDialog.labels.displayOff")
        : t("deviceDetailDialog.labels.displayOn"),
      () => foyerApi.setScreenPower(premiseId, screen.id, !displayOn)
    );

  // Latest delivered diagnostic artifacts (commands are newest-first).
  const latestShot = commands.find(
    (c) =>
      c.type === "screenshot" &&
      c.status === "acked" &&
      typeof c.result?.assetUrl === "string"
  );
  const latestLogs = commands.find(
    (c) =>
      c.type === "log_capture" &&
      c.status === "acked" &&
      typeof c.result?.assetUrl === "string"
  );
  const shotUrl = latestShot?.result?.assetUrl as string | undefined;
  const logsUrl = latestLogs?.result?.assetUrl as string | undefined;
  // A capture the device TRIED and could not deliver.
  //
  // Without this the dialog renders identically whether a device never got the
  // command, never captured, or was refused by the upload endpoint - it simply
  // shows no artifact. That silence cost a bench session on 2026-08-12
  // ("I see SENT on capture logs ... but I don't see any log"), so a device
  // that reported why now gets to say so here. Only the newest attempt, and
  // only when it is newer than the artifact on screen - an old failure next to
  // a fresh capture would read as a live fault.
  const latestFailure = commands.find(
    (c) =>
      (c.type === "log_capture" || c.type === "screenshot") &&
      c.status === "acked" &&
      typeof c.result?.error === "string"
  );
  // Compared against the artifact of the SAME kind: a screenshot failure used
  // to be hidden by any newer log file, and vice versa.
  const latestOfFailedKind =
    latestFailure?.type === "screenshot" ? latestShot : latestLogs;
  const failureNote =
    latestFailure &&
    (!latestOfFailedKind ||
      new Date(latestFailure.createdAt) >
        new Date(latestOfFailedKind.createdAt))
      ? t(
          latestFailure.type === "screenshot"
            ? "deviceDetailDialog.captureFailed.screenshot"
            : "deviceDetailDialog.captureFailed.log",
          { error: String(latestFailure.result?.error) }
        )
      : null;
  // Whether a screenshot from THIS box can be trusted as a picture of the panel.
  // Shown next to the artifacts rather than buried in the capability chips: it is
  // the caveat that decides how to read the image right above it.
  const fidelityNote = screenshotFidelityNote(
    t,
    live?.platform,
    live?.capabilities
  );

  const changeOrientation = (next: DoorSignOrientation) => {
    if (!screen || next === orientation) return;
    void withBusy(t("deviceDetailDialog.labels.setOrientation"), async () => {
      await foyerApi.setScreenOrientation(premiseId, screen.id, next);
      setOrientation(next);
      onChanged?.();
    });
  };

  const regenerateHotspot = () =>
    withBusy(t("deviceDetailDialog.labels.regenerateHotspot"), async () => {
      const next = await foyerApi.regenerateGuestHotspot(premiseId, screen.id);
      setHotspot(next);
    });

  // Re-attach window: a serial alone can't re-bind a TV (it's printed on the
  // chassis, so it's an identifier, not a credential). This is the operator
  // override for a recovery the automatic factors can't cover - deliberately
  // short-lived, one device at a time, and audited.
  const allowReattach = () =>
    withBusy(
      t("deviceDetailDialog.labels.allowReattach"),
      async () => {
        const res = await foyerApi.openReattachWindow(premiseId, screen.id, 15);
        toast.success(
          t("deviceDetailDialog.reattachAllowed", {
            device: deviceName,
            until: fmt.time(res.until, { timeStyle: "medium" }),
          }),
          { durationMs: 6000 }
        );
      },
      true
    );

  const unpair = async () => {
    if (!confirm(t("deviceDetailDialog.unpairConfirm"))) return;
    try {
      await foyerApi.unpairScreen(premiseId, screen.id);
    } catch (err) {
      toast.error(
        describeError(t, "unpairDevice", err, { device: deviceName })
      );
      return;
    }
    toast.success(t("deviceDetailDialog.unpaired", { device: deviceName }));
    onChanged?.();
    onClose();
  };

  return (
    // One device, with its Role / Orientation toggles and actions: editing an
    // existing thing, so it slides in from the right. Two columns -> 900px.
    <EditDrawer
      open={open}
      onClose={onClose}
      width={900}
      title={
        screen.roomId
          ? t("deviceDetailDialog.title", { room: screen.roomId })
          : t("deviceDetailDialog.unassigned")
      }
      subtitle={
        <Typography
          component="span"
          variant="caption"
          sx={{
            fontFamily: "fontFamilyMono",
            color: "text.secondary",
          }}
        >
          {screen.id}
        </Typography>
      }
      headerAside={
        <Chip
          size="small"
          color={online ? "success" : "default"}
          label={
            online
              ? t("devicesView.status.online")
              : t("devicesView.status.offline")
          }
          sx={{ mt: "4px" }}
        />
      }
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={3}
        alignItems="flex-start"
      >
        {/* Left: telemetry. Both columns need an explicit basis - the
              actions column carries a long help paragraph, and left to size
              itself it takes the whole row and wraps telemetry values one
              character per line. */}
        <Box sx={{ flex: "1 1 300px", minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            {t("deviceDetailDialog.sections.telemetry")}
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "max-content 1fr",
              gridAutoRows: "max-content",
              columnGap: 2,
              rowGap: 0.75,
              mt: 1,
            }}
          >
            <Row
              label={t("common:labels.status")}
              value={
                online
                  ? t("deviceDetailDialog.online")
                  : t("deviceDetailDialog.offline")
              }
            />
            {/*
                ⚠️ EVERY `live?.*` ROW BELOW IS A POINT-IN-TIME READING, NOT A
                CURRENT ONE - and until 2026-09-09 nothing on this dialog said
                so. Firmware, platform, app/bundle versions, uptime, IPs and
                signal all come from the last `ScreenSnapshot` the device
                wrote; only Room, Serial and Model are current facts held by
                the CMS itself.

                What that cost: hasselbacken/302 was read as a healthy device
                "with model and firmware and all" while it had in fact been
                silent for 22 hours and was sitting on the pairing screen. The
                dialog was showing a fresh Model beside day-old firmware in
                identical type, with "Offline" one row up and a timestamp
                fifteen rows down, and nothing joining them.

                Same rule as the ESG figures in CLAUDE.md: a number nobody can
                date is a number that gets believed. So when the device is not
                online, the readings are labelled with their age, once, at the
                top of the block they apply to.
              */}
            {!online && live && (
              <Row
                label={t("deviceDetailDialog.rows.readings")}
                value={t("deviceDetailDialog.readingsAge", {
                  ago: relativeTime(t, live.lastSeenAt),
                })}
                tone="warn"
              />
            )}
            <Row
              label={t("deviceDetailDialog.rows.power")}
              value={formatPower(t, fmt, live?.powerState, live?.powerStateAt)}
            />
            <Row
              label={t("deviceDetailDialog.rows.unit")}
              value={screen.roomId ?? "-"}
            />
            {allowDoorSign && (
              <Row
                label={t("deviceDetailDialog.rows.role")}
                value={roleLabel(t, role)}
              />
            )}
            {/*
                What the wall is ACTUALLY doing, straight from the panel.
                Shown only for a signage screen and only once one has reported:
                every other role has nothing to say here, and an empty "Playback
                -" row on a bedroom TV would just be noise.
              */}
            {playback && (
              <Row
                label={t("deviceDetailDialog.rows.playback")}
                value={
                  playback.detail
                    ? `${playback.text} — ${playback.detail}`
                    : playback.text
                }
                tone={playback.severity}
              />
            )}
            <Row
              label={t("deviceDetailDialog.rows.model")}
              value={
                live?.model ?? screen.model ?? t("deviceDetailDialog.unknown")
              }
            />
            <Row
              label={t("deviceDetailDialog.rows.platform")}
              value={platformLabel(live?.platform)}
            />
            <Row
              label={t("deviceDetailDialog.rows.serial")}
              value={screen.serial ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.firmware")}
              value={live?.firmware ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.resolution")}
              value={live?.resolution ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.appVersion")}
              value={live?.appVersion ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.management")}
              value={managementLabel(t, live?.platform, live?.capabilities)}
            />
            <Row
              label={t("deviceDetailDialog.rows.webBundle")}
              value={live?.webVersion ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.streamingApp")}
              value={live?.streamingAppVersion ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.userAgent")}
              value={live?.userAgent ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.uptime")}
              value={formatUptime(t, live?.uptimeMs)}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.wifiIp")}
              value={live?.wifiIp ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.ethernetIp")}
              value={live?.ethIp ?? "-"}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.wifiSignal")}
              value={formatSignal(t, live?.wifiSignal)}
              mono
            />
            <Row
              label={t("deviceDetailDialog.rows.lastSeen")}
              value={
                screen.lastSeenAt
                  ? fmt.dateTime(screen.lastSeenAt, {
                      dateStyle: "short",
                      timeStyle: "medium",
                    })
                  : t("devicePresentation.never")
              }
            />
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">
            {t("deviceDetailDialog.sections.capabilities")}
          </Typography>
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {capabilities.length ? (
              capabilities.map((c) => (
                <Chip
                  key={c}
                  size="small"
                  variant="outlined"
                  label={capabilityLabel(t, c)}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                -
              </Typography>
            )}
          </Box>

          {showHotspot && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                {t("deviceDetailDialog.sections.guestCasting")}
              </Typography>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "max-content 1fr",
                  gridAutoRows: "max-content",
                  columnGap: 2,
                  rowGap: 0.75,
                  mt: 1,
                }}
                data-testid="device-guest-casting"
              >
                <Row
                  label={t("deviceDetailDialog.rows.hotspotSsid")}
                  value={hotspot?.ssid ?? "-"}
                  mono
                />
                <Row
                  label={t("deviceDetailDialog.rows.hotspotPassword")}
                  value={hotspot?.password ?? "-"}
                  mono
                />
              </Box>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">
            {t("deviceDetailDialog.sections.onlineHistory")}
          </Typography>
          <Box sx={{ mt: 1 }}>
            <UptimeChart events={events} days={14} />
          </Box>

          {canSeeHistory && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                {t("deviceDetailDialog.sections.deviceHistory")}
              </Typography>
              <Box sx={{ mt: 1 }}>
                {history.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t("deviceDetailDialog.historyEmpty")}
                  </Typography>
                ) : (
                  <Stack spacing={0.75}>
                    {history.map((h, i) => (
                      <Stack
                        key={`${h.at}-${i}`}
                        direction="row"
                        spacing={1}
                        alignItems="baseline"
                        flexWrap="wrap"
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ minWidth: 130 }}
                        >
                          {fmt.dateTime(h.at, {
                            dateStyle: "short",
                            timeStyle: "medium",
                          })}
                        </Typography>
                        <Typography variant="body2">
                          {describeHistory(t, h)}
                        </Typography>
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Box>
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">
            {t("deviceDetailDialog.sections.diagnostics")}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {shotUrl ? (
              <a href={shotUrl} target="_blank" rel="noopener noreferrer">
                <Box
                  component="img"
                  src={shotUrl}
                  alt={t("deviceDetailDialog.latestScreenshot")}
                  sx={{
                    width: "100%",
                    maxWidth: 360,
                    borderRadius: 1,
                    border: "1px solid",
                    borderColor: "divider",
                    display: "block",
                  }}
                />
              </a>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("deviceDetailDialog.noScreenshot")}
              </Typography>
            )}
            {latestShot?.createdAt && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
                data-testid="screenshot-provenance"
              >
                {t("deviceDetailDialog.captured", {
                  at: fmt.dateTime(latestShot.createdAt, {
                    dateStyle: "short",
                    timeStyle: "medium",
                  }),
                  mode: captureModeLabel(t, latestShot.result?.captureMode),
                })}
              </Typography>
            )}
            {logsUrl && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <a href={logsUrl} target="_blank" rel="noopener noreferrer">
                  {t("deviceDetailDialog.downloadLogs")}
                </a>
                {latestLogs?.result?.captureMode === "in-app" && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block" }}
                  >
                    {t("deviceDetailDialog.consoleOnly")}
                  </Typography>
                )}
              </Typography>
            )}
            {failureNote && (
              <Typography
                variant="caption"
                color="error.main"
                sx={{ display: "block", mt: 1 }}
                data-testid="capture-failure-note"
              >
                {failureNote}
              </Typography>
            )}
            {fidelityNote && (
              <Typography
                variant="caption"
                color="warning.main"
                sx={{ display: "block", mt: 1 }}
                data-testid="screenshot-fidelity-note"
              >
                {fidelityNote}
              </Typography>
            )}
          </Box>

          <PermissionGate resource="screens" level="administrate" place={here}>
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                {t("deviceDetailDialog.sections.crashes")}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <DeviceExitsPanel
                  premiseId={premiseId}
                  screenId={screen.id}
                  platform={live?.platform}
                />
              </Box>

              <Divider sx={{ my: 2 }} />
              <Typography variant="overline" color="text.secondary">
                {t("deviceDetailDialog.sections.deviceLog")}
              </Typography>
              <Box sx={{ mt: 1 }}>
                <DeviceLogPanel premiseId={premiseId} screenId={screen.id} />
              </Box>
            </>
          </PermissionGate>

          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">
            {t("deviceDetailDialog.sections.recentCommands")}
          </Typography>
          <Box sx={{ mt: 1 }}>
            {commands.length ? (
              <Stack spacing={0.75}>
                {commands.map((c) => (
                  <Stack
                    key={c.commandId}
                    direction="row"
                    alignItems="center"
                    spacing={1}
                  >
                    <Chip
                      size="small"
                      color={commandChipColor(c)}
                      variant={c.status === "acked" ? "filled" : "outlined"}
                      label={outcomeLabel(t, c)}
                    />
                    <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                      {commandLabel(t, c)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {fmt.time(c.createdAt, { timeStyle: "medium" })}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t("deviceDetailDialog.noCommands")}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Right: actions */}
        <Stack
          spacing={1}
          sx={{
            flex: "0 1 340px",
            minWidth: { xs: 0, md: 260 },
            width: "100%",
          }}
        >
          {(allowDoorSign || allowSignage) && (
            <>
              <Typography variant="overline" color="text.secondary">
                {t("deviceDetailDialog.sections.role")}
              </Typography>
              {/* Read-only (Sigge, 2026-10-19): the room's type decides what a
                  screen is. Change it by changing the room's type, or by
                  moving the screen to another room. */}
              <Typography variant="body2" data-testid="device-role">
                {roleLabel(t, role)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {screen.roomId
                  ? t("deviceDetailDialog.roleSetBy", { room: screen.roomId })
                  : t("deviceDetailDialog.roleSetByUnconnected")}
              </Typography>
              {role === "door-sign" && (
                <Typography variant="caption" color="text.secondary">
                  {t("deviceDetailDialog.doorSignNote")}
                </Typography>
              )}
              {role === "door-sign" && (
                <>
                  <Typography variant="overline" color="text.secondary">
                    {t("deviceDetailDialog.sections.orientation")}
                  </Typography>
                  <PermissionGate
                    resource="screens"
                    level="administrate"
                    place={here}
                  >
                    <ToggleButtonGroup
                      size="small"
                      exclusive
                      fullWidth
                      value={orientation}
                      onChange={(_e, v) =>
                        v && changeOrientation(v as DoorSignOrientation)
                      }
                      disabled={busy !== null}
                      aria-label={t("deviceDetailDialog.orientationLabel")}
                      data-testid="device-orientation-toggle"
                    >
                      <ToggleButton
                        value="landscape"
                        data-testid="device-orientation-landscape"
                      >
                        {t("deviceDetailDialog.landscape")}
                      </ToggleButton>
                      <ToggleButton
                        value="portrait"
                        data-testid="device-orientation-portrait"
                      >
                        {t("deviceDetailDialog.portrait")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </PermissionGate>
                  <Typography variant="caption" color="text.secondary">
                    {t("deviceDetailDialog.orientationHint")}
                  </Typography>
                </>
              )}
            </>
          )}

          {role === "signage" && (
            <>
              <Typography variant="overline" color="text.secondary">
                {t("deviceDetailDialog.sections.advertising")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                data-testid="device-vistar-venue-note"
              >
                {screen.roomId
                  ? t("deviceDetailDialog.vistarVenue", { room: screen.roomId })
                  : t("deviceDetailDialog.vistarNoUnit")}
              </Typography>
            </>
          )}

          <Typography variant="overline" color="text.secondary">
            {t("deviceDetailDialog.sections.actions")}
          </Typography>
          <PermissionGate resource="screens" level="edit" place={here}>
            <Button
              startIcon={<RestartAltIcon />}
              variant="outlined"
              onClick={reboot}
              disabled={busy !== null}
              data-testid="device-reboot"
            >
              {t("deviceDetailDialog.buttons.reboot")}
            </Button>
          </PermissionGate>
          {/* Only the CAVEAT. "Restarts the device" is what the button says
                it does, so saying it again underneath is noise - but a box that
                can only reload the page cannot recover the frozen WebView an
                operator is usually reaching for it to fix, and nothing else on
                this screen says so.

                Reworded 2026-09-09. It used to name device-owner enrolment as
                the reason, which is the ANDROID reason and reads as a
                configuration detail rather than a limit. On a Tizen panel the
                cause is different (no native bridge at all) and the
                consequence is bigger: a reload re-runs the current document,
                so it cannot bring a panel back into its widget origin either.
                An operator pressing this four times on hasselbacken/302 got
                four green acks and no restart. Say the limit, not the cause. */}
          {!live?.capabilities?.includes("rebootDevice") && (
            <Typography
              variant="caption"
              color="text.secondary"
              data-testid="device-reboot-caption"
            >
              {t("deviceDetailDialog.rebootCaption")}
            </Typography>
          )}
          <PermissionGate resource="screens/messages" level="edit" place={here}>
            <Button
              startIcon={<MessageIcon />}
              variant="outlined"
              onClick={sendMessage}
              disabled={busy !== null}
              data-testid="device-message"
            >
              {t("deviceDetailDialog.labels.sendMessage")}
            </Button>
          </PermissionGate>
          {canPower && (
            <>
              <PermissionGate resource="screens" level="edit" place={here}>
                <Button
                  startIcon={<PowerSettingsNewIcon />}
                  variant="outlined"
                  onClick={togglePower}
                  disabled={busy !== null}
                  data-testid="device-power"
                >
                  {displayOn
                    ? t("deviceDetailDialog.labels.displayOff")
                    : t("deviceDetailDialog.labels.displayOn")}
                </Button>
              </PermissionGate>
            </>
          )}
          <PermissionGate resource="screens" level="edit" place={here}>
            <Button
              startIcon={<RefreshIcon />}
              variant="outlined"
              onClick={refreshTelemetry}
              disabled={busy !== null}
              data-testid="device-refresh-telemetry"
            >
              {t("deviceDetailDialog.labels.refreshTelemetry")}
            </Button>
          </PermissionGate>
          <PermissionGate resource="screens" level="administrate" place={here}>
            <Button
              startIcon={<PhotoCameraIcon />}
              variant="outlined"
              onClick={takeScreenshot}
              disabled={busy !== null}
              data-testid="device-screenshot"
            >
              {t("deviceDetailDialog.buttons.captureScreenshot")}
            </Button>
          </PermissionGate>
          <PermissionGate resource="screens" level="administrate" place={here}>
            <Button
              startIcon={<DescriptionIcon />}
              variant="outlined"
              onClick={captureLogs}
              disabled={busy !== null}
              data-testid="device-capture-logs"
            >
              {t("deviceDetailDialog.labels.captureLogs")}
            </Button>
          </PermissionGate>
          {canRestoreClone && (
            <PermissionGate
              resource="screens"
              level="administrate"
              place={here}
            >
              <Button
                startIcon={<DescriptionIcon />}
                variant="outlined"
                onClick={() => setCloneOpen(true)}
                disabled={busy !== null}
                data-testid="device-restore-clone"
              >
                {t("deviceDetailDialog.buttons.restoreClone")}
              </Button>
            </PermissionGate>
          )}
          <Button
            startIcon={<OpenInNewIcon />}
            variant="outlined"
            onClick={() => onOpenPreview(screen, role)}
            data-testid="device-open-preview"
          >
            {role === "signage"
              ? t("deviceDetailDialog.buttons.openSignagePreview")
              : t("deviceDetailDialog.buttons.openPreview")}
          </Button>
          {showHotspot && (
            <PermissionGate resource="screens" level="edit" place={here}>
              <Button
                startIcon={<WifiTetheringIcon />}
                variant="outlined"
                onClick={regenerateHotspot}
                disabled={busy !== null}
                data-testid="device-regenerate-hotspot"
              >
                {t("deviceDetailDialog.labels.regenerateHotspot")}
              </Button>
            </PermissionGate>
          )}
          <PermissionGate resource="screens" level="administrate" place={here}>
            <Button
              startIcon={<KeyIcon />}
              variant="outlined"
              onClick={allowReattach}
              disabled={busy !== null || !screen.serial}
              title={
                screen.serial
                  ? t("deviceDetailDialog.reattachHint")
                  : t("deviceDetailDialog.reattachNoSerial")
              }
              data-testid="device-allow-reattach"
            >
              {t("deviceDetailDialog.buttons.allowReattach")}
            </Button>
          </PermissionGate>
          <Divider sx={{ my: 1 }} />
          <PermissionGate resource="screens" level="administrate" place={here}>
            <Button
              startIcon={<LinkOffIcon />}
              color="error"
              variant="outlined"
              onClick={unpair}
              disabled={busy !== null}
              data-testid="device-unpair"
            >
              {t("devicePresentation.commands.unpair")}
            </Button>
          </PermissionGate>
          {busy && (
            <Typography variant="caption" color="text.secondary">
              {t("deviceDetailDialog.busy", { label: busy })}
            </Typography>
          )}
        </Stack>
      </Stack>
      <RestoreCloneDialog
        open={cloneOpen}
        premiseId={premiseId}
        screenId={screen.id}
        device={{
          model: live?.model ?? null,
          firmware: live?.firmware ?? null,
        }}
        onClose={() => setCloneOpen(false)}
        onPushed={(commandId) => void onClonePushed(commandId)}
      />
    </EditDrawer>
  );
}

function Row({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: string;
  mono?: boolean;
  /** Colours the VALUE only. A fault has to be legible in a wall of grey
   *  telemetry rows, and colouring the label would just shout the word. */
  tone?: "ok" | "warn" | "error";
}) {
  return (
    <>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ alignSelf: "baseline" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontFamily: mono
            ? 'ui-monospace, "SF Mono", Menlo, Consolas, monospace'
            : undefined,
          wordBreak: "break-all",
          color:
            tone === "error"
              ? "error.main"
              : tone === "warn"
                ? "warning.main"
                : undefined,
          fontWeight: tone && tone !== "ok" ? "fontWeightSemibold" : undefined,
        }}
      >
        {value}
      </Typography>
    </>
  );
}
